/**
 * Campaign picker
 *
 * Search for a campaign, limited to one organisation unless other organisations are included, or
 * enter a campaign ID directly. One row per control.
 */

import React, { useState } from 'react';
import { Button, Checkbox, Form } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { i18nKeys } from '../constants';
import CampaignSearch from './CampaignSearch';
import Styles from './CampaignPicker.module.css';

interface CampaignPickerProps {
  disabled?: boolean;
  idPrefix: string;
  label: string;
  onPick: (campaignId: number) => void;
  organizationId?: number | null;
  organizationName?: string | null;
}

const CampaignPicker: React.FC<CampaignPickerProps> = ({
  disabled,
  idPrefix,
  label,
  onPick,
  organizationId,
  organizationName
}) => {
  const [includeOtherOrganisations, setIncludeOtherOrganisations] =
    useState(false);
  const [campaignIdText, setCampaignIdText] = useState('');

  const limitToOrganisation = !!organizationId && !includeOtherOrganisations;

  const addById = () => {
    const campaignId = Number(campaignIdText.trim());
    if (Number.isInteger(campaignId) && campaignId > 0) {
      onPick(campaignId);
      setCampaignIdText('');
    }
  };

  return (
    <div className={Styles.picker}>
      <CampaignSearch
        id={`${idPrefix}-search`}
        label={label}
        disabled={disabled}
        organizationId={limitToOrganisation ? organizationId : null}
        onPick={onPick}
      />

      {!!organizationId && (
        <Form.Field>
          <Checkbox
            id={`${idPrefix}-include-other`}
            toggle
            disabled={disabled}
            checked={includeOtherOrganisations}
            label={I18n.t(i18nKeys.PICKER.INCLUDE_OTHER_ORGANISATIONS)}
            onChange={(_e, data) =>
              setIncludeOtherOrganisations(!!data.checked)
            }
          />
          {limitToOrganisation && organizationName && (
            <p className="field-description">
              <Translate
                value={i18nKeys.PICKER.ORGANISATION_ONLY}
                organisation={organizationName}
              />
            </p>
          )}
        </Form.Field>
      )}

      <Form.Field>
        <label htmlFor={`${idPrefix}-id`}>
          <Translate value={i18nKeys.PICKER.ID} />
        </label>
        <div className={Styles.idRow}>
          <input
            id={`${idPrefix}-id`}
            type="number"
            min={1}
            step={1}
            disabled={disabled}
            value={campaignIdText}
            onChange={(e) => setCampaignIdText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addById();
              }
            }}
          />
          <Button
            type="button"
            disabled={disabled || !campaignIdText.trim()}
            onClick={addById}
          >
            <Translate value={i18nKeys.PICKER.ADD_BY_ID} />
          </Button>
        </div>
      </Form.Field>
    </div>
  );
};

export default CampaignPicker;
